import React, { useMemo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { getUserFromStorage, canAccessFeature, canAccessMyTasks, canAccessOrganization } from "./authorization";

const DEV = process.env.NODE_ENV === "development";

const LOG_PREFIX = "[RouteAutoGuard]";

const logWarning = (message, context = {}) => {
  if (DEV) {
    console.warn(`${LOG_PREFIX} ${message}`, context);
  }
};

const sanitizeUser = (user) => user ? { ...user, password: undefined } : null;

const checkAuth = (user, location) => {
  if (!user) {
    logWarning(`No user found, redirecting to /login from: ${location.pathname}`);
    return { authenticated: false, redirectTo: "/login", state: { from: location.pathname } };
  }
  return { authenticated: true };
};

const checkMyTasksAccess = (user, meta, fallback) => {
  const isMyTasksRoute = meta.feature === "pages.my_tasks" || meta.page === "my_tasks";
  if (!isMyTasksRoute) return { allowed: true };

  if (!canAccessMyTasks(user)) {
    logWarning(`User does not have access to My Tasks, redirecting to: ${fallback}`);
    return { allowed: false, redirectTo: fallback };
  }

  if (meta.requireNurseTasks === true) {
    const roleSlug = user?.role_slug || "";
    const isNurseTasksRole = roleSlug.includes("nurse") && roleSlug.includes("basic");

    if (!isNurseTasksRole) {
      logWarning(`Nurse tasks required but user role does not qualify, redirecting to: ${fallback}`);
      return { allowed: false, redirectTo: fallback };
    }
  }

  return { allowed: true };
};

const checkFeatureAccess = (user, meta) => {
  if (!meta || Object.keys(meta).length === 0) return { allowed: true };

  if (meta.feature) {
    const hasAccess = canAccessFeature(user, meta.feature);
    if (!hasAccess) {
      return { allowed: false };
    }
  }

  return { allowed: true };
};

const checkOrganizationAccess = (user, tenantId, locationId, pathname) => {
  const hasAccess = canAccessOrganization(user, tenantId, locationId);
  if (!hasAccess) {
    logWarning(`Organization/location access denied to ${pathname}`, {
      user: sanitizeUser(user),
      currentTenantId: tenantId,
      currentLocationId: locationId,
    });
    return {
      allowed: false,
      redirectTo: "/dashboard",
      state: { error: "You don't have access to this organization or location" }
    };
  }
  return { allowed: true };
};

const RouteAutoGuard = ({
  fallback = "/unauthorized",
  meta = {},
  children = null,
  requireAuth = true,
}) => {
  const location = useLocation();
  const user = getUserFromStorage();
  const currentTenantId = sessionStorage.getItem("acc_id");
  const currentLocationId = sessionStorage.getItem("current_loc");

  if (requireAuth) {
    const authCheck = checkAuth(user, location);
    if (!authCheck.authenticated) {
      return <Navigate to={authCheck.redirectTo} replace state={authCheck.state} />;
    }
  }
  if (meta.requiresCEO && !user.is_ceo) {
    return <Navigate to={fallback} replace />;
  }

  if (!meta || Object.keys(meta).length === 0) {
    return children || <Outlet />;
  }

  const myTasksCheck = checkMyTasksAccess(user, meta, fallback);
  if (!myTasksCheck.allowed) {
    return <Navigate to={myTasksCheck.redirectTo} replace />;
  }

  const featureCheck = checkFeatureAccess(user, meta);
  if (!featureCheck.allowed) {
    logWarning(`Access denied to ${location.pathname}`, {
      user: sanitizeUser(user),
      meta,
    });
    return <Navigate to={fallback} replace />;
  }

  // const orgCheck = checkOrganizationAccess(user, currentTenantId, currentLocationId, location.pathname);
  // if (!orgCheck.allowed) {
  //   return <Navigate to={orgCheck.redirectTo} replace state={orgCheck.state} />;
  // }

  return children || <Outlet />;
};

export function withRouteGuard({ meta, fallback = "/unauthorized" }) {
  return function (Component) {
    return function ProtectedComponent(props) {
      return (
        <RouteAutoGuard meta={meta} fallback={fallback}>
          <Component {...props} />
        </RouteAutoGuard>
      );
    };
  };
}

export function AdminGuard({ children, fallback = "/unauthorized" }) {
  return (
    <RouteAutoGuard meta={{ feature: "admin" }} fallback={fallback}>
      {children}
    </RouteAutoGuard>
  );
}

export function TOCAGuard({ children, fallback = "/unauthorized" }) {
  return (
    <RouteAutoGuard meta={{ feature: "cross_account_access" }} fallback={fallback}>
      {children}
    </RouteAutoGuard>
  );
}

export function InventoryGuard({ children, fallback = "/unauthorized" }) {
  return (
    <RouteAutoGuard meta={{ feature: "pages.inventory" }} fallback={fallback}>
      {children}
    </RouteAutoGuard>
  );
}

export function MyTasksGuard({ children, requireNurseTasks = false, fallback = "/unauthorized" }) {
  return (
    <RouteAutoGuard
      meta={{
        feature: "pages.my_tasks",
        requireNurseTasks,
      }}
      fallback={fallback}
    >
      {children}
    </RouteAutoGuard>
  );
}

export default RouteAutoGuard;

